import { useState, useEffect } from 'react';
import { Tabs, Tab, Col, Row, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { v4 as uuid } from 'uuid';

//components
import withLocalization from '~/hoc/withLocalization';
import RectangularCard from '../../../components/Card/RectangularCard';
import UserProfileLogo from "../../../components/UserProfileLogo";

//elements
import Checkbox from '../../../components/CustomCheckbox/CustomCheckbox.jsx';
import Button from '../../../components/CustomButton/CustomButton.jsx';

//utils
import { shortDateFormat, timeFormat } from '../../../utils/dateFormat'
import commonStore from '../../../library/stores/commonStore';

//assets
import swapLogo from "~/assets/img/swap-right-line.svg";

import './Schedule.css';

const ShiftCard = ({ shift, user, task, dateTimeRules, status, statusBg, cardBgClassname = "rectangle-card-bg-D5DDF2", t }) => {
    const allowCompanyShifts = commonStore?.config?.client?.allow_company_shifts;
    const companyShift = allowCompanyShifts ? commonStore.getCompanyShift(shift.company_shift_id) : null;
    return (
        <RectangularCard backgoundClass={cardBgClassname} style={{ minHeight: "20px", height: "97px" }}>
            <Col xs={3}>
                <UserProfileLogo image_id={user.user_image ? user.user_image : false} width='50px' height="50px" />
            </Col>
            <Col xs={9}>
                <Row className='shift_detail_container'>
                    <Col xs={{ span: 6, offset: 6 }} className={`status ${statusBg}`}>{status}</Col>
                    <Col xs={12} className='name'>{`${user.first_name} ${user.last_name}`}</Col>
                    <Col xs={12} className='details'>{shortDateFormat(shift.for_date, dateTimeRules)} {timeFormat(shift.start_time,)}-{timeFormat(shift.end_time, dateTimeRules)} </Col>
                    <Col xs={12} className='details'>{task.task_number ? `${task.task_number} | ${task.name}` : task.name} </Col>
                    {companyShift && <Col xs={12} className='details'>{t('Shift')}: {companyShift.shift_name} ({companyShift.short_code})</Col>}
                </Row>
            </Col>
        </RectangularCard>
    )
}

const AbsenceOrVacationCard = ({ absenceOrVacation, user, t, dateTimeRules }) => {
    return (
        <RectangularCard backgoundClass="rectangle-card-bg-F6DFDF" style={{ minHeight: "20px", height: "90px" }}>
            <Col xs={3}>
                <UserProfileLogo image_id={user.user_image ? user.user_image : false} width='50px' height="50px" />
            </Col>
            <Col xs={9}>
                <Row className='shift_detail_container'>
                    <Col xs={{ span: 5, offset: 7 }} className='status warning text-capitalize'>{t(absenceOrVacation.type)}</Col>
                    <Col xs={12} className='name'>{`${user.first_name} ${user.last_name}`}</Col>
                    <Col xs={12} className='details'>{shortDateFormat(absenceOrVacation.from_date, dateTimeRules)} - {shortDateFormat(absenceOrVacation.to_date, dateTimeRules)}</Col>
                    <Col xs={12} className='details'>{t("Type")} - {absenceOrVacation.absence_type ? `${absenceOrVacation.absence_type} | ${absenceOrVacation.leave_type}` : absenceOrVacation.leave_type} </Col>
                </Row>
            </Col>
        </RectangularCard>
    )
}

const NewShifts = ({ data, usersMap, tasksMap, t, dateTimeRules }) => {
    return (
        <Row className='justify-content-center'>
            {data.map(_shift => {
                return (
                    <Col xs={4} key={uuid()}>
                        <ShiftCard shift={_shift}
                            status={t("Assigned")}
                            user={usersMap[_shift.assignee_id]}
                            task={tasksMap[_shift.task_id]}
                            dateTimeRules={dateTimeRules}
                            statusBg={'success'}
                            t={t}
                        />
                    </Col>)
            })}
        </Row>
    )
}
const IntersectedShifts = ({ data, shiftsOverwritten, usersMap, tasksMap, t, dateTimeRules, submit, isMember, hasShiftOverride, currentUserId }) => {
    const [checkboxes, setCheckboxes] = useState([...data.map(() => false)]);
    // members without the override permission cannot overwrite a row whose existing (old) shifts include one
    // created by an admin/PM (created_by !== self); such rows are locked.
    const isRowLocked = (e) => isMember && !hasShiftOverride
        && (e.oldShifts || []).some(o => Number(o.created_by) !== Number(currentUserId));
    return (
        <Row className='justify-content-center'>
            {data.map((e, index) => {
                const _new_shift = e.newShift;
                const _old_shifts = e.oldShifts;
                const locked = isRowLocked(e);
                return (
                    <>
                        <Col xs={5} className='d-flex me-2' key={uuid()}>
                            {locked ?
                                <OverlayTrigger
                                    placement="right"
                                    overlay={
                                        <Tooltip id={`locked-checkbox-tooltip-${index}`}>
                                            {t("You don't have permission to override Admin/PM created shifts")}
                                        </Tooltip>
                                    }
                                >
                                    <span className="d-inline-block">
                                        <Checkbox
                                            inline
                                            disabled={locked}
                                            isChecked={false}
                                            onChange={() => { }}
                                        />
                                    </span>
                                </OverlayTrigger>
                                :
                                <Checkbox
                                    inline
                                    disabled={locked}
                                    isChecked={checkboxes[index]}
                                    onChange={evt => {
                                        const _checkboxes = checkboxes;
                                        _checkboxes[index] = evt;
                                        setCheckboxes([..._checkboxes]);
                                    }}
                                />
                            }
                            <div>
                                <ShiftCard shift={_new_shift}
                                    status={t(shiftsOverwritten ? "Assigned" : "Copy Failed")}
                                    user={usersMap[_new_shift.assignee_id]}
                                    task={tasksMap[_new_shift.task_id]}
                                    dateTimeRules={dateTimeRules}
                                    statusBg={shiftsOverwritten ? 'success' : 'danger'}
                                    cardBgClassname={'rectangle-card-bg-DFF7E9'}
                                    t={t}
                                />
                            </div>
                        </Col>
                        <Col xs={2} key={uuid()}>
                            <div><img src={swapLogo} alt="swap"></img></div>
                        </Col>
                        <Col xs={4} key={uuid()}>
                            {_old_shifts.map(_old_shift => {
                                return (<ShiftCard
                                    shift={_old_shift}
                                    status={t("Old Shift")}
                                    user={usersMap[_old_shift.assignee_id]}
                                    task={tasksMap[_old_shift.task_id]}
                                    dateTimeRules={dateTimeRules}
                                    statusBg={'warning'}
                                    t={t}
                                    key={uuid()}
                                />
                                )
                            })}
                        </Col>
                    </>
                )
            })}
            <Col xs={8} className='mt-3'>
                <Checkbox
                    inline
                    label={t("Check All")}
                    isChecked={data.every((e, idx) => isRowLocked(e) || checkboxes[idx])}
                    onChange={evt => {
                        // locked rows (admin/PM shifts a member can't override) stay unchecked
                        const _checkboxes = data.map((e) => isRowLocked(e) ? false : evt);
                        setCheckboxes([..._checkboxes]);
                    }}
                />
                <div>
                    <p className="fw-bold" style={{ fontSize: "12px" }}>
                        {t("Note")}:&nbsp;
                        {shiftsOverwritten ?
                            t("If you think this was a mistake then please select the shifts you would like to restore and undo changes")
                            :
                            t("Please select shifts to overwrite and submit")
                        }
                    </p>
                </div>
            </Col>
            <Col xs={4} className='text-end mt-3'>
                <Button wd fill onClick={() => submit(shiftsOverwritten, checkboxes)}>
                    {t(shiftsOverwritten ? "Undo Changes" : "Overwrite Shifts")}
                </Button>
            </Col>
        </Row>
    )
}
const FailedToCopy = ({ data, usersMap, tasksMap, t, dateTimeRules }) => {
    return (
        <Row className='justify-content-center'>
            {data.map(e => {
                const _shift = e.newShift;
                const _absenceOrVacation = e.intersectingAbsencesOrVacationForCurrentShift;
                return (
                    <>
                        <Col xs={4} key={uuid()}>
                            <ShiftCard shift={_shift}
                                status={t("Copy Failed")}
                                user={usersMap[_shift.assignee_id]}
                                task={tasksMap[_shift.task_id]}
                                dateTimeRules={dateTimeRules}
                                statusBg={'danger'}
                                t={t}
                            />
                        </Col>
                        <Col xs={2} key={uuid()}>
                            <div className=""><img src={swapLogo} alt="swap"></img></div>
                        </Col>
                        <Col xs={5} key={uuid()}>
                            {e.isRecurringShift ?
                                <div className="fw-bold mt-3">{t('Recurring shifts cannot be copied.')}</div>
                                :
                                <AbsenceOrVacationCard absenceOrVacation={_absenceOrVacation}
                                    user={usersMap[_absenceOrVacation.user_id]}
                                    dateTimeRules={dateTimeRules}
                                    t={t}
                                />
                            }
                        </Col>
                    </>
                )
            })}
        </Row>
    )
}



function AfterCopyResponse(props) {
    const { afterCopyResponse, dateTimeRules, t, closeModal, isMember, hasShiftOverride, currentUserId } = props;
    const { newShifts, overwrittenShifts, possibleShiftsForOverwrite, shiftsFailedToCopy, usersMap, tasksMap } = afterCopyResponse;

    useEffect(() => {
        const tabs = {};
        if (possibleShiftsForOverwrite.length) {
            tabs['possibleShiftsForOverwrite'] = false;
        }
        if (shiftsFailedToCopy.length) {
            tabs['shiftsFailedToCopy'] = false;
        }
        if (Object.keys(tabs).length) {
            props.updateVisitedFailedStatusTabs(tabs);
        }
    }, [])

    const handleTabVisits = (tabName) => {
        const _hasVisitedFailedStatusTabs = props.hasVisitedFailedStatusTabs;
        if (_hasVisitedFailedStatusTabs[tabName] !== undefined) {
            _hasVisitedFailedStatusTabs[tabName] = true;
            props.updateVisitedFailedStatusTabs(_hasVisitedFailedStatusTabs);
        }
    }

    const submit = (shiftsOverwritten, checkboxes) => {
        let _newShifts = [], _oldShifts = [];
        if (shiftsOverwritten) {
            overwrittenShifts.forEach((e, index) => {
                if (checkboxes[index]) {
                    _newShifts.push(e.newShift);
                    _oldShifts.push(...e.oldShifts);
                }
            });
        } else {
            possibleShiftsForOverwrite.forEach((e, index) => {
                if (checkboxes[index]) {
                    _newShifts.push(e.newShift);
                    _oldShifts.push(...e.oldShifts);
                }
            })
        }
        closeModal({
            newShifts: _newShifts,
            oldShifts: _oldShifts,
            overwriteShifts: !shiftsOverwritten
        })
    }

    if (!afterCopyResponse) return <></>;
    return (
        <Tabs defaultActiveKey="success" id="afterCopyResponse" onSelect={(e) => handleTabVisits(e)}>
            <Tab eventKey="success" title={t('New Shifts')}>
                <h5 className='fw-600'>{t('Following shifts has been copied and assigned successfully')}.</h5>
                <NewShifts
                    data={newShifts}
                    usersMap={usersMap}
                    tasksMap={tasksMap}
                    t={t}
                    dateTimeRules={dateTimeRules}
                />
            </Tab>
            {!!shiftsFailedToCopy.length &&
                <Tab eventKey="shiftsFailedToCopy" title={<><i className='fa fa-exclamation-circle text-danger' />&nbsp;{t('Copy Failed')}</>}>
                    <h5 className='fw-600'>{t('These shifts could not be copied as they intersect with absence or vacations')}.</h5>
                    <FailedToCopy
                        data={shiftsFailedToCopy}
                        usersMap={usersMap}
                        tasksMap={tasksMap}
                        t={t}
                        dateTimeRules={dateTimeRules}
                    />
                </Tab>
            }
            {!!overwrittenShifts.length &&
                <Tab eventKey="overwritten" title={t('Overwritten shifts')}>
                    <h5 className='fw-600'>{t('Following shifts has been copied and replaced the old ones')}.</h5>
                    <IntersectedShifts
                        shiftsOverwritten={true}
                        data={overwrittenShifts}
                        usersMap={usersMap}
                        tasksMap={tasksMap}
                        t={t}
                        dateTimeRules={dateTimeRules}
                        submit={submit}
                    />
                </Tab>
            }
            {!!possibleShiftsForOverwrite.length &&
                <Tab eventKey="possibleShiftsForOverwrite" title={<><i className='fa fa-exclamation-circle text-danger' />&nbsp;{t('Existing Shifts')}</>}>
                    <h5 className='fw-600'>{t('These shifts could not be copied as the old shifts timings intersects')}.</h5>
                    <IntersectedShifts
                        shiftsOverwritten={false}
                        data={possibleShiftsForOverwrite}
                        usersMap={usersMap}
                        tasksMap={tasksMap}
                        t={t}
                        dateTimeRules={dateTimeRules}
                        submit={submit}
                        isMember={isMember}
                        hasShiftOverride={hasShiftOverride}
                        currentUserId={currentUserId}
                    />
                </Tab>
            }
        </Tabs>
    )
}

function AfterBulkCreateFailedResponse(props) {
    const { shiftsFailedToCreate, dateTimeRules, t, message } = props;
    if (!shiftsFailedToCreate) return <></>;
    return (
        <>
            <h5 className='fw-600'>{message ? message : `${t('These shifts could not be created as they intersect with absence or vacations or existing shifts')}.`}</h5>
            <Row className='justify-content-center'>
                {shiftsFailedToCreate.map(e => {
                    const _shift = e.newShift;
                    const _intersectingEntity = e.instersectingEntity;
                    return (
                        <>
                            <Col xs={4} key={uuid()}>
                                <ShiftCard shift={_shift}
                                    status={t("Failed")}
                                    user={_shift.user}
                                    task={_shift.task}
                                    dateTimeRules={dateTimeRules}
                                    statusBg={'danger'}
                                    t={t}
                                />
                            </Col>
                            <Col xs={2} key={uuid()}>
                                <div className=""><img src={swapLogo} alt="swap"></img></div>
                            </Col>
                            <Col xs={5} key={uuid()}>
                                {_intersectingEntity.type === 'shift' ?
                                    <ShiftCard shift={_intersectingEntity}
                                        status={t("Old Shift")}
                                        user={_intersectingEntity.user}
                                        task={_intersectingEntity.task}
                                        dateTimeRules={dateTimeRules}
                                        t={t}
                                        statusBg={'warning'}
                                    />
                                    :
                                    <AbsenceOrVacationCard
                                        absenceOrVacation={_intersectingEntity}
                                        user={_intersectingEntity.user}
                                        dateTimeRules={dateTimeRules}
                                        t={t}
                                    />
                                }
                            </Col>
                        </>
                    )
                })}
            </Row>
        </>
    )
}

export const AfterBulkCreateFailedResponseModalBody = withLocalization(AfterBulkCreateFailedResponse);

function BulkShiftSuccessSummaryComponent({ data, dateTimeRules, onClose, t, className }) {
    const { shifts, usersMap, shiftTask } = data;
    if (!shifts || !shifts.length) return null;

    const groupedByUser = {};
    shifts.forEach(shift => {
        const id = shift.assignee_id;
        if (!groupedByUser[id]) {
            const user = usersMap[id];
            groupedByUser[id] = {
                name: user ? `${user.first_name} ${user.last_name}` : `#${id}`,
                allDates: [],
                timeStr: `${timeFormat(shift.start_time, dateTimeRules)}-${timeFormat(shift.end_time, dateTimeRules)}`
            };
        }
        groupedByUser[id].allDates.push(new Date(shift.for_date));
    });

    const userEntries = Object.values(groupedByUser);
    const taskLabel = shiftTask?.name || '';

    return (
        <div className={`p-0 ${className || ''}`}>
            <div className="d-flex align-items-center justify-content-between mb-2 pb-2 border-bottom">
                <div className="d-flex align-items-center">
                    <i className="fa fa-check-circle text-success me-2" style={{ fontSize: '16px' }}></i>
                    <div>
                        <h6 className="fw-bold mb-0" style={{ fontSize: '13px' }}>{t('Bulk shifts created successfully')}</h6>
                        {taskLabel && <div className="text-muted small" style={{ fontSize: '10px' }}>{taskLabel}</div>}
                    </div>
                </div>
            </div>

            <div className="overflow-auto border rounded mb-2" style={{ maxHeight: '280px' }}>
                {userEntries.map((entry) => {
                    const sorted = entry.allDates.sort((a, b) => a - b);
                    const start = sorted[0];
                    const end = sorted[sorted.length - 1];
                    const isSameDay = start.getTime() === end.getTime();

                    return (
                        <div key={uuid()} className="d-flex align-items-center py-2 px-2 border-bottom" style={{ fontSize: '11px' }}>

                            <div className="text-truncate" style={{ flex: '1.5', minWidth: '0' }}>
                                <span className="fw-bold text-dark">{entry.name}</span>
                                <span className="text-muted ms-2" style={{ fontSize: '10px' }}>({entry.timeStr})</span>
                            </div>

                            <div className="text-center px-2" style={{ flex: '1.5' }}>
                                <span className="text-success fw-600">
                                    {shortDateFormat(start, dateTimeRules)}
                                    {!isSameDay && <><span className="text-muted mx-1">→</span>{shortDateFormat(end, dateTimeRules)}</>}
                                </span>
                            </div>

                            <div className="text-end d-flex align-items-center justify-content-end" style={{ flex: '1' }}>
                                <span className="text-dark me-2" style={{ fontSize: '9px', fontWeight: 'normal' }}>
                                    {entry.allDates.length} {t('shifts')}
                                </span>
                                <i className="fa fa-check text-success"></i>
                            </div>

                        </div>
                    );
                })}
            </div>
        </div>
    );
}
export const BulkShiftSuccessSummary = BulkShiftSuccessSummaryComponent;

export default withLocalization(AfterCopyResponse)